// src/services/apaleo/auto-assign.ts
// Extracted verbatim from src/services/apaleo.ts (Phase 5 of
// plans/monolith-migration-plan.md). `this` is typed via ThisType<ApaleoClient>.

import i18n from "../../i18n";
import type { UnitModel } from './types';
import type { ApaleoClient } from './client-contract';
import { IS_DEV, MockData, errorMessage } from './config';

const part: ThisType<ApaleoClient> = {
async autoAssignPreferredRoom(
  reservationId: string,
  preferredRoomName: string,
): Promise<{ success: boolean; assignedUnitId: string | null; message: string }> {
  if (!reservationId || !preferredRoomName) {
    return {
      success: false,
      assignedUnitId: null,
      message: i18n.t('common.error'),
    };
  }

  const cleanPreferredName = String(preferredRoomName).trim();
  if (!cleanPreferredName) {
    return {
      success: false,
      assignedUnitId: null,
      message: i18n.t('common.error'),
    };
  }

  if (IS_DEV) {
    console.log(
      `ApaleoAuth (Mock): autoAssignPreferredRoom for Res: ${reservationId}, Room: ${cleanPreferredName}`,
    );
    // Load reservation details
    const reservation =
      MockData.MOCK_RESERVATIONS.find((r) => r.id === reservationId) ||
      MockData.MOCK_RESERVATIONS[0];
    const propertyId = reservation.property?.id || "BERLIN";

    // Load property mock units
    const units =
      MockData.MOCK_UNITS[propertyId] || MockData.MOCK_UNITS.BERLIN;

    // Match case-insensitively and trim-wise
    const matchedUnit = units.find(
      (u) =>
        u.name &&
        u.name.trim().toLowerCase() === cleanPreferredName.toLowerCase(),
    );

    if (matchedUnit) {
      return {
        success: true,
        assignedUnitId: matchedUnit.id ?? null,
        message: `Wunschzimmer ${matchedUnit.name} erfolgreich zugewiesen (Mock).`,
      };
    } else {
      const fallbackUnit = units[0];
      if (fallbackUnit) {
        return {
          success: true,
          assignedUnitId: fallbackUnit.id ?? null,
          message: `Wunschzimmer ${cleanPreferredName} existiert nicht. Alternativzimmer ${fallbackUnit.name} wurde automatisch zugewiesen (Mock).`,
        };
      } else {
        return {
          success: false,
          assignedUnitId: null,
          message: `Wunschzimmer ${cleanPreferredName} existiert nicht und kein Alternativzimmer verfügbar (Mock).`,
        };
      }
    }
  }

  try {
    // 1. Fetch reservation details
    const reservation = await this.fetchReservationById(reservationId);
    if (!reservation) {
      return {
        success: false,
        assignedUnitId: null,
        message: i18n.t('common.error'),
      };
    }

    const propertyId = reservation.property?.id;
    const arrival = reservation.arrival;
    const departure = reservation.departure;
    const unitGroupId = reservation.unitGroup?.id;

    if (!propertyId || !arrival || !departure) {
      return {
        success: false,
        assignedUnitId: null,
        message: i18n.t('common.error'),
      };
    }

    // 2. Load the specific unit matching the user-friendly preferred room name via textSearch
    const queryUrl = `/inventory/v1/units?propertyId=${propertyId}&textSearch=${encodeURIComponent(cleanPreferredName)}`;
    const unitData = (await this._fetch(queryUrl)) as
      | { units?: UnitModel[] }
      | null;
    const matchedUnit = unitData?.units?.find(
      (u) =>
        u.name &&
        u.name.trim().toLowerCase() === cleanPreferredName.toLowerCase(),
    );

    if (matchedUnit) {
      // Try direct assignment
      try {
        await this._fetch(
          `/booking/v1/reservation-actions/${reservationId}/assign-unit/${matchedUnit.id}`,
          {
            method: "PUT",
          },
        );
        return {
          success: true,
          assignedUnitId: matchedUnit.id ?? null,
          message: `Wunschzimmer ${matchedUnit.name} erfolgreich zugewiesen.`,
        };
      } catch (directAssignError: unknown) {
        console.warn(
          `Direkte Zuweisung für Wunschzimmer ${matchedUnit.name} (${matchedUnit.id}) fehlgeschlagen: ${errorMessage(directAssignError)}. Suche Alternativzimmer...`,
        );
      }
    } else {
      console.warn(
        `Wunschzimmer mit Name ${cleanPreferredName} wurde in Property ${propertyId} nicht gefunden. Suche Alternativzimmer...`,
      );
    }

    // 3. Fallback: Find vacant unit of the same category (unitGroupId)
    if (!unitGroupId) {
      return {
        success: false,
        assignedUnitId: null,
        message: `Wunschzimmer ${matchedUnit ? matchedUnit.name : cleanPreferredName} belegt/ungültig und Kategorie (UnitGroup) der Reservierung ist unbekannt.`,
      };
    }

    // Filter units matching category
    const units = await this.fetchUnits(propertyId);
    const categoryUnits = units.filter(
      (u) => u.unitGroupId === unitGroupId || u.unitGroup?.id === unitGroupId,
    );
    if (categoryUnits.length === 0) {
      return {
        success: false,
        assignedUnitId: null,
        message: `Wunschzimmer belegt/ungültig und keine Zimmer der Kategorie ${unitGroupId} in Property ${propertyId} konfiguriert.`,
      };
    }

    // Load all overlapping active reservations. Scoped to THIS property (P.6 fix):
    // the unscoped call paginated across every allowed property (up to
    // maxPages x 100 rows per property) just to compute occupancy for one unit
    // group in one property.
    const overlappingReservations = await this.fetchReservations(
      arrival,
      departure,
      "Stay",
      '',
      '',
      propertyId,
    );

    const occupiedUnitIds = overlappingReservations
      .filter(
        (res) =>
          res.property?.id === propertyId &&
          res.status !== "Canceled" &&
          res.status !== "NoShow" &&
          res.id !== reservationId &&
          res.unit?.id,
      )
      .map((res) => res.unit?.id || "");

    const vacantUnits = categoryUnits.filter(
      (u) => u.id !== undefined && !occupiedUnitIds.includes(u.id),
    );

    if (vacantUnits.length === 0) {
      return {
        success: false,
        assignedUnitId: null,
        message: `Wunschzimmer ${matchedUnit ? matchedUnit.name : cleanPreferredName} ist belegt/ungültig und keine freien Alternativzimmer in der Kategorie ${unitGroupId} verfügbar.`,
      };
    }

    const fallbackUnit = vacantUnits[0];
    await this._fetch(
      `/booking/v1/reservation-actions/${reservationId}/assign-unit/${fallbackUnit.id}`,
      {
        method: "PUT",
      },
    );

    return {
      success: true,
      assignedUnitId: fallbackUnit.id ?? null,
      message: matchedUnit
        ? `Wunschzimmer ${matchedUnit.name} war belegt. Alternativzimmer ${fallbackUnit.name || fallbackUnit.id} wurde automatisch zugewiesen.`
        : `Wunschzimmer ${cleanPreferredName} existiert nicht. Alternativzimmer ${fallbackUnit.name || fallbackUnit.id} wurde automatisch zugewiesen.`,
    };
  } catch (error: unknown) {
    console.error(
      `Fehler bei der automatischen Wunschzimmer-Zuweisung:`,
      error,
    );
    return {
      success: false,
      assignedUnitId: null,
      message: `Automatische Wunschzimmer-Zuweisung fehlgeschlagen: ${errorMessage(error)}`,
    };
  }
},

};

export default part;
